import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Loading } from '../Loading/Loading';
import { TrialExpiredModal } from '../TrialExpiredModal/TrialExpiredModal';
import { SubscriptionExpiredModal } from '../SubscriptionExpiredModal/SubscriptionExpiredModal';
import { InactiveAccountOverlay } from '../InactiveAccountOverlay/InactiveAccountOverlay';

export const AuthGuard = () => {
  const user = useAppSelector((state) => state.auth?.user);
  const isLoading = useAppSelector((state) => state.auth?.isLoading);
  const business = useAppSelector((state) => state.business);
  const isBusinessListFetched = useAppSelector((state) => state.business.isListFetched);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const [showSubscriptionExpiredModal, setShowSubscriptionExpiredModal] = useState(false);
  const [hasSeenTrialWarning, setHasSeenTrialWarning] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (
      location.pathname === '/sign-up-confirmation' ||
      location.pathname.startsWith('/admin') ||
      location.pathname.startsWith('/accept-invite') ||
      searchParams.get('redirectBusinessId')
    ) {
      return;
    }

    if (!isBusinessListFetched) {
      // Do not check if business list is not locaded yet
      return;
    }

    if (!business.selected) {
      const redirectBusinessId = searchParams.get('redirectBusinessId');
      const selectedBusinessId = localStorage.getItem('businessId') ?? redirectBusinessId ?? null;

      if (selectedBusinessId) {
        const existsInList = business.list?.some(
          (b) => b.id === selectedBusinessId
        );

        if (existsInList) {
          dispatch({
            type: 'business/selectBusiness',
            payload: { id: selectedBusinessId },
          });
        } else if (!isLoading) {
          localStorage.removeItem('businessId');

          if (business.list?.length === 1) {
            dispatch({
              type: 'business/selectBusiness',
              payload: { id: business.list[0].id },
            });
          } else if ((business.list?.length ?? 0) > 1) {
            navigate('/select-business');
          } else {
            const pendingInviteToken = localStorage.getItem('pendingInviteToken');
            if (!pendingInviteToken) {
              navigate('/select-business/create-new');
            }
          }
        }
      } else {
        if (business?.list?.length === 1) {
          dispatch({
            type: 'business/selectBusiness',
            payload: { id: business.list[0].id },
          });
        }
        if (business?.list?.length === 0) {
          const pendingInviteToken = localStorage.getItem('pendingInviteToken');
          if (!pendingInviteToken) {
            navigate('/select-business/create-new');
          }
        }
      }
    }
  }, [business.list, business.selected, dispatch, navigate, location.pathname, isLoading, isBusinessListFetched]);

  useEffect(() => {
    const hasAccessToken = localStorage.getItem('accessToken');

    // Only redirect to login if there's no user AND no access token AND not loading
    // If there's an access token but no user, AnimationLayout will handle loading the user
    if (!user && !hasAccessToken && !isLoading) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  // Check if user has already seen trial warning for current business
  useEffect(() => {
    if (business.selected?.id) {
      const businessId = business.selected.id;
      const hasSeenWarning = localStorage.getItem(`trialWarningSeen_${businessId}`) === 'true';
      setHasSeenTrialWarning(hasSeenWarning);
    }
  }, [business.selected?.id]);

  // Check for trial status and show warnings or handle restrictions
  useEffect(() => {
    if (!user || !business.selected) return;

    // Find the subscription for the current business
    const businessSubscription = user.subscriptions?.find(
      (sub) => sub.businessId === business.selected?.id
    );
    const hasSeenWarning =
      localStorage.getItem(
        `trialWarningSeen_${businessSubscription?.businessId}`
      ) === 'true';
    setHasSeenTrialWarning(() => hasSeenWarning);
    if (
      businessSubscription?.trialEnd !== undefined &&
      businessSubscription?.trialEnd !== null
    ) {
      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
      const trialEndTime = businessSubscription.trialEnd;
      const timeRemaining = trialEndTime - currentTime;
      const daysRemaining = Math.floor(timeRemaining / (24 * 60 * 60)); // Convert to days

      // Handle expired trials (daysRemaining < 0)
      if (daysRemaining < 0) {
        if (location.pathname.startsWith('/settings')) {
          // Allow access to settings page
          return;
        } else if (Math.abs(daysRemaining) > 3) {
          // Trial expired more than 3 days ago - show modal
          if (location.pathname !== '/') {
            // Redirect to dashboard and show modal
            navigate('/');
            setShowTrialExpiredModal(true);
          } else if (!showTrialExpiredModal) {
            // Show modal if on dashboard but modal not open
            setShowTrialExpiredModal(true);
          }
        } else {
          // Trial expired up to 3 days ago - redirect to trial expired page
          if (location.pathname !== '/trial-expired') {
            navigate('/trial-expired');
          }
        }
      } else {
        // Trial is still active - show warning pages as one-time notifications
        // Only show warning if user hasn't seen it yet and not on settings or already on trial warning pages
        const isOnTrialWarningPage =
          location.pathname === '/trial-ending' ||
          location.pathname === '/trial-ending-last-day';
        if (
          !hasSeenWarning &&
          !location.pathname.startsWith('/settings') &&
          !isOnTrialWarningPage
        ) {
          const businessId = business.selected?.id;
          if (businessId) {
            if (daysRemaining === 1 || daysRemaining === 0) {
              navigate('/trial-ending-last-day');
              setHasSeenTrialWarning(true);
              localStorage.setItem(`trialWarningSeen_${businessId}`, 'true');
            } else if (daysRemaining === 2 || daysRemaining === 3) {
              navigate('/trial-ending');
              setHasSeenTrialWarning(true);
              localStorage.setItem(`trialWarningSeen_${businessId}`, 'true');
            }
          }
        }
      }
    }

    if (
      businessSubscription &&
      !businessSubscription.trialEnd &&
      businessSubscription.endDate
    ) {
      const currentTime = new Date();
      const subscriptionEndDate = new Date(businessSubscription.endDate);

      if (currentTime > subscriptionEndDate) {
        if (location.pathname.startsWith('/settings')) {
          return;
        }
        if (location.pathname !== '/') {
          navigate('/');
          setShowSubscriptionExpiredModal(true);
        } else if (!showSubscriptionExpiredModal) {
          setShowSubscriptionExpiredModal(true);
        }
      }
    }
  }, [
    user,
    business.selected,
    navigate,
    location.pathname,
    hasSeenTrialWarning,
  ]);

  // Show loading state while user data is being fetched
  if (isLoading) {
    return <Loading />;
  }

  const inactiveModalOpen = !!user && user?.status === 'INACTIVE';

  return (
    <>
      <Outlet />
      <TrialExpiredModal
        isOpen={showTrialExpiredModal}
        onClose={() => setShowTrialExpiredModal(false)}
      />
      <SubscriptionExpiredModal
        isOpen={!inactiveModalOpen && showSubscriptionExpiredModal}
        onClose={() => setShowSubscriptionExpiredModal(false)}
      />
      <InactiveAccountOverlay
        isOpen={inactiveModalOpen}
        onClose={() => dispatch({ type: 'auth/logout' })}
      />
    </>
  );
};
